import { clsx } from 'clsx';
import { memo, useId, useState } from 'react';
import type { ProductCard as ProductCardData } from '@sbg/shared/schemas';
import { Picture } from '@/components/media/Picture';
import { StoneSkeleton } from '@/components/media/StoneSkeleton';
import { AppLink } from '@/components/ui/AppLink';
import { buttonClasses } from '@/components/ui/button-classes';
import { useFormatters, useT } from '@/lib/i18n';
import { SpecularSurface } from '@/motion/SpecularSurface';
import { PriceBlock } from './PriceBlock';
import { productFacts } from './product-utils';
import { useSharedElement } from './shared-element';
import { WhatsAppButton } from './WhatsAppButton';
import { badgesShown, saveShown } from '@/lib/catalog/storefront';
import { WishlistButton } from './WishlistButton';

/**
 * Width of a card's photo at each layout (matches the grid in `product.css`: 1 column < 600 px,
 * 2 < 1024, 3 < 1600, 4 above; content capped at 1240 px).
 */
export const PRODUCT_CARD_SIZES =
  '(min-width: 1600px) 300px, (min-width: 1024px) 400px, (min-width: 600px) 50vw, 100vw';

export interface ProductCardProps {
  product: ProductCardData;
  /** Cards in the first row load their photo eagerly (it is the LCP image on `/shop`). */
  priority?: boolean;
  sizes?: string;
  className?: string;
}

/**
 * The shop card (DESIGN_SYSTEM §2.3). Every card shows the same things, always:
 *
 *   photo (the close-up swaps in on hover / keyboard focus) · name · colour, finish, origin and
 *   thickness · price, discount and final price — or "Price on request" · a two-line description ·
 *   a wishlist heart · and three actions: **Details** (the product page), **Enquiry** (the contact
 *   form with this product pre-filled) and **WhatsApp** (a chat pre-filled with name, SKU and link).
 *
 * The photo is one big tap target for pointer users but is hidden from the keyboard and screen
 * readers — the product name and the Details button are the real links to the same page.
 */
export const ProductCard = memo(function ProductCard({
  product,
  priority = false,
  sizes = PRODUCT_CARD_SIZES,
  className,
}: ProductCardProps) {
  const t = useT();
  const { formatNumber } = useFormatters();
  const nameId = useId();
  const { imageRef, titleRef, activate } = useSharedElement<HTMLDivElement, HTMLHeadingElement>();
  // The close-up is only fetched once someone points at (or tabs into) the card: a page of 24 cards
  // would otherwise download 24 extra photos that touch screens never show.
  const [macroArmed, setMacroArmed] = useState(false);
  const armMacro = (): void => {
    setMacroArmed(true);
  };

  const name = product.name;
  const description = product.shortDescription;
  const href = `/shop/${product.slug}`;
  const facts = productFacts(
    product,
    ({ min, max }) =>
      t('product.thickness', {
        mm: min === max ? formatNumber(min) : `${formatNumber(min)}–${formatNumber(max)}`,
      }),
    formatNumber,
  );
  const badges: { key: string; label: string; tone?: 'muted' }[] = [];
  if (product.isNew) badges.push({ key: 'new', label: t('product.new') });
  if (product.isFeatured) badges.push({ key: 'featured', label: t('product.featured') });
  if (product.availability !== 'IN_STOCK') {
    badges.push({
      key: 'availability',
      label: t(`availability.${product.availability}`),
      tone: 'muted',
    });
  }

  return (
    <article
      className={clsx('product-card', className)}
      data-availability={product.availability}
      aria-labelledby={nameId}
      onPointerEnter={armMacro}
      onFocus={armMacro}
    >
      <SpecularSurface className="product-card__media">
        <AppLink
          to={href}
          className="product-card__media-link"
          tabIndex={-1}
          aria-hidden="true"
          onClick={activate}
        >
          <div ref={imageRef} className="product-card__photo">
            {product.cover ? (
              <Picture
                image={product.cover}
                sizes={sizes}
                priority={priority}
                alt=""
                className="product-card__img"
              />
            ) : (
              <StoneSkeleton tone="still" className="product-card__img product-card__empty">
                <span>{t('product.noImage')}</span>
              </StoneSkeleton>
            )}
            {product.macro && macroArmed ? (
              <Picture
                image={product.macro}
                sizes={sizes}
                alt=""
                className="product-card__img product-card__macro"
              />
            ) : null}
          </div>
        </AppLink>
        {/* D-365: no badges on the photograph — the tile is the picture; kept for when `badgesShown()` is on again */}
        {badgesShown() && badges.length > 0 ? (
          <ul className="product-card__badges">
            {badges.map((badge) => (
              <li key={badge.key} className="badge" data-tone={badge.tone}>
                {badge.label}
              </li>
            ))}
          </ul>
        ) : null}
      </SpecularSurface>

      <div className="product-card__body">
        <p className="product-card__category">{product.category.name}</p>
        <h3 ref={titleRef} id={nameId} className="product-card__name">
          <AppLink to={href} onClick={activate}>
            {name}
          </AppLink>
        </h3>

        {facts.length > 0 ? (
          <ul className="product-card__facts">
            {facts.map((fact) => (
              <li key={fact.key} className="chip" data-fact={fact.key}>
                {fact.swatch ? (
                  <span
                    className="chip__swatch"
                    style={{ backgroundColor: fact.swatch }}
                    aria-hidden="true"
                  />
                ) : null}
                {fact.label}
              </li>
            ))}
          </ul>
        ) : null}

        <PriceBlock price={product.price} />

        {/* the stock line of the storefront layout (D-362; hidden with the badges, D-365); the other states show as the badge on the photo */}
        {badgesShown() && product.availability === 'IN_STOCK' ? (
          <p className="product-card__stock">{t('availability.IN_STOCK')}</p>
        ) : null}

        {description ? <p className="product-card__desc">{description}</p> : null}

        <div className="product-card__actions">
          <AppLink
            to={href}
            className={buttonClasses({
              variant: 'primary',
              size: 'sm',
              className: 'product-card__details',
            })}
            aria-label={t('product.detailsAria', { name })}
            onClick={activate}
          >
            {t('common.details')}
          </AppLink>
          <AppLink
            to={{ pathname: '/contact-us', search: `?product=${product.slug}`, hash: '#enquiry' }}
            className={buttonClasses({ variant: 'glass', size: 'sm' })}
            aria-label={t('product.enquiryAria', { name })}
          >
            {t('common.enquiry')}
          </AppLink>
          <WhatsAppButton size="sm" product={{ name, sku: product.sku, slug: product.slug }} />
        </div>
      </div>

      {/* D-363: no Save heart for now; the button is kept for when `saveShown()` is on again */}
      {saveShown() ? (
        <WishlistButton productId={product.id} name={name} className="product-card__wish" />
      ) : null}
    </article>
  );
});
